import { useEffect } from 'react'
import { useFormContext, FieldValues, Path } from 'react-hook-form'

interface OptionalInputProps<TFieldValues extends FieldValues> {
  /**
   *  If true, the fields are displayed. Their values and dirty state are reset.
   *  If false, the fields disappear. Their values and dirty state are removed from the form.
   */
  condition: boolean

  /**
   * Names of form field. They will be dynamically registered and unregistered to the form.
   * You can use the name of an object or array field and all it's nested values will also be handled.
   * Be sure to include in this array all form fields that are used in children.
   */
  fieldNames: Path<TFieldValues>[]
  children: React.ReactElement
}

/**
 * HOC that handles registering and unregistering fields to the form based on a given condition.
 * It also recovers the dirty state after re-registering and renders it's children based on the condition.
 * It is important that unregistered fields do not render again as they will register themselves to the field on mount.
 */
export const OptionalFields = <TFieldValues extends FieldValues>({
  fieldNames,
  condition,
  children,
}: OptionalInputProps<TFieldValues>): JSX.Element | null => {
  const { setValue, unregister, getValues } = useFormContext<TFieldValues>()

  useEffect(() => {
    if (condition) {
      // reset the dirty state on mount
      for (const name of fieldNames) {
        setValue(name, getValues(name), { shouldDirty: true })
      }
    } else {
      // remove the field from the form's dirty state on unmount
      for (const name of fieldNames) {
        unregister(name, {
          keepDefaultValue: true,
          keepValue: true,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, unregister, getValues, setValue, ...fieldNames])

  if (!condition) return null

  return children
}
